import React, { useState } from "react";
import {
  Table, Button, Space, Tag, message, Popconfirm,
  Form, Input, Drawer, Image, Upload, Select
} from 'antd'
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useApiSelector } from 'src/hooks'
import { useRequest, dispatchAsync } from 'friday-async'
import jsCookie from 'js-cookie'
import config from 'src/config'

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

const NEWS_IMAGE_SIZE = { width: 420, height: 262, label: '资讯图片 420×262' }

const validateNewsImageSize = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new window.Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      if (img.width !== NEWS_IMAGE_SIZE.width || img.height !== NEWS_IMAGE_SIZE.height) {
        message.error(
          `图片尺寸不符：当前 ${img.width}×${img.height}，要求 ${NEWS_IMAGE_SIZE.label}（${NEWS_IMAGE_SIZE.width}×${NEWS_IMAGE_SIZE.height}）`,
          5
        )
        resolve(false)
      } else {
        resolve(true)
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      message.error('图片读取失败')
      resolve(false)
    }
    img.src = url
  })
}

const News = () => {
  const apis = useApiSelector();
  const { dataArray, isValidating, revalidate } = useRequest(apis.news.list(), {
    paginated: false,
  })

  const [drawer, setDrawer] = useState<{ visible: boolean; info: any }>({
    visible: false, info: null
  })
  const [uploading, setUploading] = useState(false)
  const [uploading2, setUploading2] = useState(false)
  const [form] = Form.useForm()
  const isEdit = !!drawer.info

  // ====== CRUD ======
  const handleCreate = () => setDrawer({ visible: true, info: null })

  const handleEdit = (record: any) => {
    setDrawer({ visible: true, info: record })
  }

  const handleSubmit = async () => {
    try {
      const vals = await form.validateFields()
      let error: any
      if (isEdit) {
        const r = await dispatchAsync(apis.news.update({ _id: drawer.info._id, ...vals }))
        error = r.error
      } else {
        const r = await dispatchAsync(apis.news.create(vals))
        error = r.error
      }
      if (error) return message.error(isEdit ? '编辑失败' : '创建失败')
      message.success(isEdit ? '编辑成功' : '创建成功')
      setDrawer({ visible: false, info: null })
      form.resetFields()
      revalidate()
    } catch (err) { console.log(err) }
  }

  const handleRemove = async (_id: string) => {
    const { error } = await dispatchAsync(apis.news.remove({ _id }))
    if (error) return message.error('删除失败')
    message.success('删除成功')
    revalidate()
  }

  // ====== 图片上传 ======
  const handleUpload = (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading(true)
      return
    }
    if (info.file.status === 'done') {
      setUploading(false)
      const res = info.file.response
      if (res?.data?.url) {
        form.setFieldsValue({ displayImage: res.data.url })
        message.success('图片上传成功')
      }
    } else if (info.file.status === 'error') {
      setUploading(false)
      message.error('图片上传失败')
    }
  }

  const handleUpload2 = (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading2(true)
      return
    }
    if (info.file.status === 'done') {
      setUploading2(false)
      const res = info.file.response
      if (res?.data?.url) {
        form.setFieldsValue({ contentImage: res.data.url })
        message.success('图片上传成功')
      }
    } else if (info.file.status === 'error') {
      setUploading2(false)
      message.error('图片上传失败')
    }
  }

  const displayImage = Form.useWatch('displayImage', form)
  const contentImage = Form.useWatch('contentImage', form)

  // ====== 表格列 ======
  const columns = [
    { title: '标题', dataIndex: 'title', key: 'title', width: 200 },
    {
      title: '展示图',
      dataIndex: 'displayImage',
      key: 'displayImage',
      width: 100,
      render: (v: string) => v ? (
        <Image
          src={`${config.imgHost}${v.startsWith('/') ? v.slice(1) : v}`}
          alt="展示图"
          width={60}
          height={36}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          preview={{ mask: '预览' }}
        />
      ) : <Tag>-</Tag>,
    },
    {
      title: '内容图',
      dataIndex: 'contentImage',
      key: 'contentImage',
      width: 100,
      render: (v: string) => v ? (
        <Image
          src={`${config.imgHost}${v.startsWith('/') ? v.slice(1) : v}`}
          alt="内容图"
          width={60}
          height={36}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          preview={{ mask: '预览' }}
        />
      ) : <Tag>-</Tag>,
    },
    { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 160 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (v: string) => (
        <Tag color={v === 'enabled' ? 'green' : 'red'}>
          {v === 'enabled' ? '显示' : '隐藏'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除该资讯？" onConfirm={() => handleRemove(record._id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Button type="primary" ghost icon={<PlusOutlined />} onClick={handleCreate}>
        新建资讯
      </Button>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={dataArray}
        loading={isValidating}
        pagination={false}
      />

      {/* 新增/编辑 抽屉 */}
      <Drawer
        title={isEdit ? '编辑资讯' : '新建资讯'}
        width={520}
        open={drawer.visible}
        onClose={() => {
          setDrawer({ visible: false, info: null })
          form.resetFields()
        }}
        destroyOnClose
        footer={
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button style={{ width: 120 }} onClick={() => {
              setDrawer({ visible: false, info: null })
              form.resetFields()
            }}>取消</Button>
            <Button style={{ width: 120 }} type="primary" onClick={handleSubmit}>确认</Button>
          </Space>
        }
      >
        <Form {...layout} form={form}
          initialValues={isEdit ? drawer.info : {}}
        >
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入资讯标题' }]}>
            <Input placeholder="如 五一特惠活动" />
          </Form.Item>
          <Form.Item name="displayImage" label="展示图"
            extra="推荐尺寸：420×262，其他尺寸将被拒绝"
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Upload
                  name="image"
                  showUploadList={false}
                  headers={{
                    Authorization: `Bearer ${jsCookie.get('token_jwt') || ''}`
                  }}
                  action={`${config.host}/api/platform/activity/uploadImage`}
                  beforeUpload={validateNewsImageSize}
                  onChange={handleUpload}
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>上传</Button>
                </Upload>
              </Space>
              {displayImage && (
                <Image
                  src={`${config.imgHost}${displayImage.startsWith('/') ? displayImage.slice(1) : displayImage}`}
                  alt="预览"
                  style={{ maxWidth: 200, maxHeight: 100, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
                  preview={{ mask: '预览' }}
                />
              )}
            </Space>
          </Form.Item>
          <Form.Item name="contentImage" label="内容图">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Upload
                  name="image"
                  showUploadList={false}
                  headers={{
                    Authorization: `Bearer ${jsCookie.get('token_jwt') || ''}`
                  }}
                  action={`${config.host}/api/platform/activity/uploadImage`}
                  onChange={handleUpload2}
                >
                  <Button icon={<UploadOutlined />} loading={uploading2}>上传</Button>
                </Upload>
              </Space>
              {contentImage && (
                <Image
                  src={`${config.imgHost}${contentImage.startsWith('/') ? contentImage.slice(1) : contentImage}`}
                  alt="预览"
                  style={{ maxWidth: 200, maxHeight: 100, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
                  preview={{ mask: '预览' }}
                />
              )}
            </Space>
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue="enabled">
            <Select
              options={[
                { label: '显示', value: 'enabled' },
                { label: '隐藏', value: 'disabled' },
              ]}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </Space>
  )
}

export default News
